import { useRouter } from 'next/router';
import Dialog from '../../../Dialog';
import theme from '../../../theme';
import { __t } from '../../../i18n';
import { Button, Text } from '../../../styles';
import { useEffect, useRef, useState } from 'react';
import UsePlayerProps from '../../../Store/usePlayerProps';
import { VideoContentData } from '../../Player/PlayerModels';
import { ErrorBox } from '../../Player/styles';
import { sendVideoErrorDiagnostic, VideoErrorReportRequest } from '../../../apiService';
import AlertService from 'common/AlertService';

const ErrorModal = ({ reportBody, isShort }: { reportBody?: VideoErrorReportRequest | null; isShort: boolean }) => {
  const playerGlobalState = UsePlayerProps((state) => state.playerState);
  const { contentUrl } = playerGlobalState.contentData as VideoContentData;
  const router = useRouter();
  const [isMobile, setIsMobile] = useState(false);
  const [hideReportBtn, setHideReportBtn] = useState(false);

  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    handleMobileBreakpoint();
    window.addEventListener('resize', throttledHandle, { passive: true });
    return () => {
      window.removeEventListener('resize', throttledHandle);
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const throttledHandle = () => {
    if (resizeTimerRef.current !== null) return;
    resizeTimerRef.current = setTimeout(() => {
      handleMobileBreakpoint();
      resizeTimerRef.current = null;
    }, 100);
  };

  const handleMobileBreakpoint = () => {
    setIsMobile(Number(theme.breakpoints.md) > Number(window.innerWidth));
  };

  const handleSendErrorDiagnosis = async () => {
    if (!reportBody) return;
    await sendVideoErrorDiagnostic(reportBody)
      .then(() => {
        setHideReportBtn(true);
        AlertService.success(__t('comments.feedback.success'));
        handleReload();
      });
  };

  const handleReload = () => {
    router.reload();
  };

  const handleBack = () => {
    if (isShort) {
      router.back();
      return;
    }
    router.replace(contentUrl);
  };

  return (
    <Dialog open={true} backColor={theme.palette.background[700][0]} customWidth={isMobile ? '320px' : '432px'} radius="1.5rem"
      hasWidth={true}>
      <ErrorBox direction="column" justify="center" align="center">
        <Text variant="h2" as="div" mb="1.5rem">{__t('player.play_err_title')}</Text>
        <Text variant="body1" as="div">{__t('player.play_err')}</Text>
        <Button fullWidth variant="secondary" mt={isMobile ? '1rem' : '2rem'} onClick={handleReload}>
          {__t('player.retry')}
        </Button>
        {!hideReportBtn && reportBody && <Button
          fullWidth variant="warning" mt={isMobile ? '1rem' : '2rem'} onClick={handleSendErrorDiagnosis}>
          {__t('player.send.feedback')}
        </Button>}
        <Button fullWidth variant="flat" mt={isMobile ? '0.5rem' : '1rem'} onClick={handleBack}>
          {__t('player.back')}
        </Button>
      </ErrorBox>
    </Dialog>
  );
};

export default ErrorModal;
